// src/pages/Pricing.js
import React, { useState, useEffect, useMemo } from "react";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

export default function Pricing() {
    const { user, refreshUser } = useAuth() || {};
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [synced, setSynced] = useState(false);

    // format helper
    const nextBilling = useMemo(() => {
        const ts = user?.current_period_end;
        if (!ts) return null;
        try {
            return new Date(ts * 1000).toLocaleString([], { month: "short", day: "numeric", year: "numeric" });
        } catch {
            return null;
        }
    }, [user?.current_period_end]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const st = params.get("status");
        if (st === "success" || st === "portal") {
            handleSync();
        }
        
    }, []);

    const handleSync = async () => {
        try {
            setBusy(true);
            setErr("");
            await api.post("/api/billing/sync");
            if (typeof refreshUser === "function") await refreshUser();
            setSynced(true);
            // Hard refresh once to make sure gates/UI are all fresh
            setTimeout(() => window.location.replace("/pricing"), 250);
        } catch (e) {
            setErr("Couldn�t sync billing. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const subscribe = async () => {
        setErr("");
        setBusy(true);
        try {
            const t = localStorage.getItem("authToken") || "";
            const { data } = await api.post(
                "/api/stripe/create-checkout-session",
                {},
                { headers: { Authorization: `Bearer ${t}` } }
            );
            window.location.href = data.url;
        } catch (e) {
            setErr("Unable to start checkout. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const manage = async () => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post("/api/stripe/create-portal-session");
            window.location.href = data.url;
        } catch (e) {
            setErr("Unable to open billing portal.");
        } finally {
            setBusy(false);
        }
    };

    

    const isPro =
        user?.subscription_tier === "pro" &&
        (user?.subscription_status === "active" || user?.subscription_status === "trialing");

    return (
        <div className="max-w-3xl mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6">Upgrade to Pro</h1>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Free */}
                <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
                    <h2 className="text-xl font-semibold mb-2">Free</h2>
                    <p className="text-gray-300 mb-4">Basic access.</p>
                    <ul className="text-sm text-gray-400 list-disc pl-5 space-y-1 mb-6">
                        <li>Login required</li>
                        <li>Limited features</li>
                    </ul>
                    <button disabled className="w-full py-2 rounded-xl bg-gray-700 text-gray-300">
                        Current
                    </button>
                </div>

                {/* Pro */}
                <div className="bg-gray-800 border border-blue-700 rounded-2xl p-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold mb-2">Pro</h2>
                        {isPro && nextBilling && (
                            <span className="text-xs text-gray-300">
                                Next billing: <span className="font-semibold">{nextBilling}</span>
                            </span>
                        )}
                    </div>
                    <div className="text-4xl font-bold mb-1">
                        $10<span className="text-lg font-medium">/mo</span>
                    </div>
                    <p className="text-gray-300 mb-4">
                        Unlock Top Trades, All NYSE Picks, Crypto &amp; Search.
                    </p>
                    <ul className="text-sm text-gray-400 list-disc pl-5 space-y-1 mb-6">
                        <li>Full access to Pro-only pages</li>
                        <li>Manage anytime via Billing Portal</li>
                    </ul>

                    {!isPro ? (
                        <button
                            disabled={busy}
                            onClick={subscribe}
                            className={`w-full py-2 rounded-xl font-semibold ${busy ? "bg-gray-600" : "bg-blue-600 hover:bg-blue-500"}`}
                        >
                            {busy ? "Redirecting�" : "Upgrade to Pro"}
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <button
                                disabled={busy}
                                onClick={manage}
                                className={`w-full py-2 rounded-xl font-semibold ${busy ? "bg-gray-600" : "bg-green-600 hover:bg-green-500"}`}
                            >
                                {busy ? "Opening�" : "Manage Billing"}
                            </button>
                        </div>
                    )}

                    {!isPro && (
                        <div className="mt-4 flex gap-3">
                            <button
                                onClick={handleSync}
                                disabled={busy}
                                className="px-4 py-2 rounded-xl bg-gray-700 hover:bg-gray-600"
                            >
                                {busy ? "Syncing�" : "Sync Billing"}
                            </button>
                            {synced && (
                                <span className="text-green-400 text-sm">Synced. If still locked, refresh.</span>
                            )}
                        </div>
                    )}

                    {err && <div className="text-red-400 text-sm mt-3">{err}</div>}
                </div>
            </div>
        </div>
    );
}

